'use client';

import { useEffect } from 'react';
import { useFocusRing, useHover, usePress } from 'react-aria';
import type { Key, ListState } from 'react-stately';
import { getFocusableTreeWalker } from '@react-aria/focus';
import {
  focusWithoutScrolling,
  mergeProps,
  useRouter,
} from '@react-aria/utils';

type UseCarouselListItemProps = {
  key: Key;
  onAction?: (key: Key) => void;
  isActivated?: boolean;
};

export function useCarouselListItem<T>(
  ref: React.RefObject<HTMLDivElement | null>,
  state: ListState<T>,
  { key, onAction, isActivated }: UseCarouselListItemProps
) {
  const item = state.collection.getItem(key);
  const firstKey = state.collection.getFirstKey();

  // @ts-expect-error FIXME(strictNullChecks)
  const isSelected = state.selectionManager.isSelected(item.key);

  const { focusProps, isFocusVisible } = useFocusRing({ within: true });

  const { hoverProps, isHovered } = useHover({});

  const router = useRouter();

  const manager = state.selectionManager;

  const isFocused = manager.focusedKey === key;

  // @ts-expect-error FIXME(strictNullChecks)
  const isLink = state.selectionManager.isLink(item.key);

  useEffect(() => {
    if (isFocused) {
      // @ts-expect-error FIXME(strictNullChecks)
      focusWithoutScrolling(ref.current);
    }
  }, [isFocused, isFocusVisible, ref]);

  const { pressProps } = usePress({
    onPressStart(e) {
      onAction?.(key);
      if (state.selectionManager.selectionMode !== 'none') {
        if (e.shiftKey) {
          manager.extendSelection(key);
        } else if (
          state.selectionManager.selectedKeys.size === 0 ||
          state.selectionManager.selectedKeys.size === 1
        ) {
          manager.replaceSelection(key);
        }
      }
    },
  });

  return {
    isSelected,
    isActivated,
    isFocusVisible,
    isHovered,
    isLink,
    gridCellProps: {
      'aria-colindex': 1,
      role: 'gridcell',
    },
    carouselItemProps: mergeProps(pressProps, hoverProps, focusProps, {
      // @ts-expect-error FIXME(strictNullChecks)
      'aria-rowindex': item.index + 1,
      // @ts-expect-error FIXME(strictNullChecks)
      'aria-selected': state.selectionManager.canSelectItem(item.key)
        // @ts-expect-error FIXME(strictNullChecks)
        ? state.selectionManager.isSelected(item.key)
        : undefined,
      // @ts-expect-error FIXME(strictNullChecks)
      'aria-disabled': state.selectionManager.isDisabled(item.key) || undefined,
      // @ts-expect-error FIXME(strictNullChecks)
      'aria-label': item.textValue,
      tabIndex:
        /** If no item is focused, make first item focusable. If an item is focused, make only that item focusable. */
        (!manager.focusedKey && key === firstKey) || manager.focusedKey === key
          ? 0
          : -1,
      onFocus() {
        manager.setFocusedKey(key);
      },
      onKeyDownCapture: (e: KeyboardEvent) => {
        if (e.key === 'tab') {
          // @ts-expect-error FIXME(strictNullChecks)
          const walker = getFocusableTreeWalker(ref.current, {
            tabbable: true,
          });
          // @ts-expect-error FIXME(strictNullChecks)
          walker.currentNode = document.activeElement;
          const next = e.shiftKey ? walker.previousNode() : walker.nextNode();
          if (next) {
            e.stopPropagation();
          }
        }
        if (e.key === 'Enter' && isLink) {
          router.open(
            // @ts-expect-error FIXME(strictNullChecks)
            ref.current,
            e,
            // @ts-expect-error FIXME(strictNullChecks)
            item.props?.href,
            // @ts-expect-error FIXME(strictNullChecks)
            item.props?.routerOptions
          );
        }
      },
    }),
  };
}
